import { useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CollegePicker } from '@backrs_ui/grow-ui/src/components/MyColleges/CollegePicker';
import { useAddUserCollegeMutation } from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.api';
import type {
  CollegePick,
  UserCollege,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

const STRINGS = appStrings.modules.myColleges;

type AddCollegeSearchProps = Readonly<{ colleges: readonly UserCollege[] }>;

export const AddCollegeSearch = ({ colleges }: AddCollegeSearchProps) => {
  const [selected, setSelected] = useState<readonly CollegePick[]>([]);
  const [addUserCollege, { isLoading: isAdding, isError: addFailed }] = useAddUserCollegeMutation();

  const add = async () => {
    const addedIds = new Set<number>();

    for (const { institution_id } of selected) {
      const result = await addUserCollege({ institution_id });

      if ('error' in result) {
        break;
      }

      addedIds.add(institution_id);
    }

    setSelected((current) => current.filter((pick) => !addedIds.has(pick.institution_id)));
  };

  return (
    <Box sx={containerSx}>
      <CollegePicker
        idPrefix="my-colleges-add"
        colleges={colleges}
        picked={selected}
        onPickedChange={setSelected}
        onSubmit={add}
        strings={{ ...STRINGS.search, onList: STRINGS.inList }}
        chipsInInput
        disableListed
        disabled={isAdding}
        searchSx={searchSx}
      >
        <Button
          id="my-colleges-add-button"
          hierarchy="Primary"
          size="md"
          disabled={selected.length === 0 || isAdding}
          onClick={add}
          sx={addButtonSx}
        >
          {STRINGS.search.add}
        </Button>
      </CollegePicker>
      {addFailed && (
        <Typography id="my-colleges-add-failed" variant="text-sm-medium" sx={errorSx}>
          {STRINGS.addFailed}
        </Typography>
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const searchSx: SxProps<Theme> = {
  flex: 1,
  width: { xs: '100%', sm: 'auto' },
};

const addButtonSx: SxProps<Theme> = {
  flexShrink: 0,
  width: { xs: '100%', sm: 'auto' },
};

const errorSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
});
